import { faviconUrl } from "@/utils/url";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { memo } from "react";

/**
 * How many favicons the row holds. Beyond that the oldest slide out, so a long
 * run keeps showing what it is finding now rather than freezing on round 1.
 */
const MAX_VISIBLE_DOMAINS = 12;
/** Seconds between two favicons of the same round appearing. */
const STAGGER_STEP_S = 0.05;

/**
 * The domains a running web search has found so far, one favicon each, the
 * newest entering last.
 *
 * A web search runs several rounds of parallel queries with an LLM planning
 * between them, and its progress reaches the chat once per round — the header
 * counters can therefore sit unchanged for the length of a whole round. These
 * favicons are what that wait has to show: they carry the same information the
 * counters do, so the row is decorative to a screen reader and hidden from it.
 */
export const ResearchDomainsRow = memo(
  ({
    domains,
    isRunning = true,
    maxVisible = MAX_VISIBLE_DOMAINS,
    className,
  }: {
    domains: readonly string[];
    /** Whether more may come: the pulsing slot after the last favicon says so. */
    isRunning?: boolean;
    /** Favicons shown before the rest are counted in a "+N". */
    maxVisible?: number;
    className?: string;
  }) => {
    const shouldReduceMotion = useReducedMotion();
    const visible = domains.slice(-maxVisible);
    const remaining = domains.length - visible.length;

    return (
      <div
        aria-hidden
        className={cn("flex items-center gap-1 px-3 pb-3 pl-9", className)}
      >
        {/* Left of the row, because it counts the favicons that have already
            slid out. To the right of the pulsing placeholder it read as a
            promise — twelve more about to arrive — rather than a tally. */}
        {remaining > 0 && (
          <span className="mr-0.5 shrink-0 text-[10px] tabular-nums text-muted-foreground/60">
            +{remaining}
          </span>
        )}
        <AnimatePresence initial={!shouldReduceMotion} mode="popLayout">
          {visible.map((domain, index) => (
            <motion.span
              key={domain}
              title={domain}
              className="flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-full border bg-muted"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={
                shouldReduceMotion
                  ? { duration: 0 }
                  : { duration: 0.25, delay: index * STAGGER_STEP_S }
              }
            >
              <img
                src={faviconUrl(domain)}
                alt=""
                loading="lazy"
                referrerPolicy="no-referrer"
                className="size-3.5"
                onError={(event) => {
                  event.currentTarget.style.visibility = "hidden";
                }}
              />
            </motion.span>
          ))}
        </AnimatePresence>
        {isRunning && (
          <span className="size-5 shrink-0 animate-pulse rounded-full border border-dashed motion-reduce:animate-none" />
        )}
      </div>
    );
  }
);
ResearchDomainsRow.displayName = "ResearchDomainsRow";
